import { UseCaseHomepage } from "@similarweb/ui-components";
import { IconButton } from "@similarweb/ui-components";
import { swSettings } from "app/common/services/swSettings";
import { AutocompleteWebCategories } from "pro-features/components/AutocompleteWebCategories/AutocompleteWebCategories";
import * as utils from "app/components/filters-bar/utils";
import { i18nFilter } from "app/filters/i18nFilter";
import { SecondaryHomePageHeaderImageUrl } from "app/UtilitiesAndConstants/Constants/Images";
import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import type { ICategory } from "app/common/services/categoryService.types";
import { mixins, colorsPalettes } from "@similarweb/styles";
import { CustomCategoriesWizard } from "app/components/customCategoriesWizard/CustomCategoriesWizard";
import { UserCustomCategoryService } from "app/services/category/userCustomCategoryService";
import categoryService from "app/common/services/categoryService";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { PopupClickContainer } from "@similarweb/ui-components";
import { allTrackers } from "app/services/track/track";

const translate = i18nFilter();
const TopComponentWrapper = styled.div`
    display: flex;
`;
const StartPageAutoCompleteWrap = styled.div`
    display: flex;
    width: 74%;
    align-items: center;
    justify-content: space-between;
    padding: 0 8px 16px 0;
`;

const LinkWrapper = styled.div`
    a {
        color: currentcolor;
        text-decoration: underline;
        cursor: pointer;
    }
`;

const Text = styled.div`
    ${mixins.setFont({ $size: 14, $weight: 400, $color: colorsPalettes.carbon[400] })};
    line-height: 23px;
`;

const ButtonWrapper = styled.div`
    margin-top: 2px;
`;

export const KeywordResearchTopKeywordsHomepage = (props: {
    title: string;
    subTitle: string;
    keywordStatePrefix?: string;
}) => {
    const { title, subTitle, keywordStatePrefix = "" } = props;
    const example1 = "digitalMarketing.findKeywords.topKeywords.link.1";
    const example2 = "digitalMarketing.findKeywords.topKeywords.link.2";
    const [selectedCategory, setSelectedCategory] = useState<ICategory>(null);
    const [showCustomCategoriesWizard, setShowCustomCategoriesWizard] = useState(false);
    const [isShowSubmitBtnTooltip, setIsShowSubmitBtnTooltip] = useState(false);
    const popupRef = useRef<PopupClickContainer>(null);

    useEffect(() => {
        let timeoutHandler;
        if (isShowSubmitBtnTooltip) {
            popupRef.current?.openPopup();
            timeoutHandler = setTimeout(() => {
                setIsShowSubmitBtnTooltip(false);
                popupRef.current?.closePopup();
            }, 3000);
        } else {
            popupRef.current?.closePopup();
        }

        return () => {
            timeoutHandler && clearTimeout(timeoutHandler);
        };
    }, [isShowSubmitBtnTooltip]);

    const onClickCategory = (category: ICategory) => {
        setSelectedCategory(category);
    };

    const availableIndustryCountries = utils.getCountries(false, swSettings.components.IndustryAnalysisTopKeywords);

    const visitKeywordGenerator = ({ category }) => {
        proNavigator.go(`${keywordStatePrefix}findkeywords_byindustry_TopKeywords`, {
            category,
            webSource: "Desktop",
            country: availableIndustryCountries[0].id,
        });
    };

    const onClickVisitKeywordGenerator = () => {
        if (!selectedCategory) {
            setIsShowSubmitBtnTooltip(true);
        } else {
            setIsShowSubmitBtnTooltip(false);
            const isCustomCategory = selectedCategory.isCustomCategory;
            const groupName = selectedCategory.isCustomCategory && selectedCategory.text;
            allTrackers.dispatchTrackEvent(
                "Search Bar",
                "click",
                `keyword/${(isCustomCategory ? groupName : selectedCategory.id) || ""}`,
            );
            visitKeywordGenerator({ category: selectedCategory.forUrl });
        }
    };

    const onExample1Click = () => {
        const category = categoryService.getCategory("Lifestyle") as ICategory;
        setSelectedCategory(category);
    };

    const onExample2Click = () => {
        const category = categoryService.getCategory("Travel_and_Tourism") as ICategory;
        setSelectedCategory(category);
    };

    const onClearSearch = () => {
        setSelectedCategory(null);
    };

    const onKeyDown = (event) => {
        const { keyCode } = event;
        const ENTER_KEY_CODE = 13;
        if (keyCode === ENTER_KEY_CODE) {
            const category = categoryService.getCategory(event.target.value, "text");
            const isCustomCategory = UserCustomCategoryService.getCustomAndSharedCategories().filter(
                (category) => category.text === event.target.value,
            );
            if (category || isCustomCategory.length > 0) {
                setSelectedCategory(event.target.value);
                onClickVisitKeywordGenerator();
            }
        }
    };

    const getTooltipContent = () => <>{translate("keywords.research.by.industry.disable.submit.tooltip")}</>;
    return (
        <>
            <CustomCategoriesWizard
                isOpen={showCustomCategoriesWizard}
                onClose={() => {
                    setShowCustomCategoriesWizard(false);
                }}
                wizardProps={{
                    stayOnPage: true,
                    onSave: () => {
                        setShowCustomCategoriesWizard(false);
                    },
                }}
            />
            <UseCaseHomepage
                icon={{
                    name: "keyword-industry",
                    size: "xxl",
                }}
                title={translate(title)}
                paddingBottom={"0"}
                titlePosition="centered"
                subtitle={translate(subTitle)}
                showSearchComponentsInTheBody={true}
                headerImageUrl={SecondaryHomePageHeaderImageUrl}
                bodyMaxWidth={675}
                searchComponents={
                    <div>
                        <TopComponentWrapper>
                            <StartPageAutoCompleteWrap onKeyDown={onKeyDown}>
                                <AutocompleteWebCategories
                                    showCustomCategoryEmptyState={true}
                                    selectedValue={selectedCategory}
                                    onClick={onClickCategory}
                                    autocompleteProps={{
                                        placeholder: i18nFilter()(
                                            "digitalMarketing.findKeywords.topKeyword.searchPlaceHolder",
                                        ),
                                    }}
                                    onClearSearch={onClearSearch}
                                    onCreateCustomCategoryClick={() => {
                                        setShowCustomCategoriesWizard(true);
                                    }}
                                />
                            </StartPageAutoCompleteWrap>
                            <PopupClickContainer
                                ref={popupRef}
                                content={() => getTooltipContent()}
                                config={{
                                    placement: "top",
                                    cssClass: "PlainTooltip-element",
                                    cssClassContent: "PlainTooltip-content",
                                    enabled: isShowSubmitBtnTooltip,
                                }}
                            >
                                <ButtonWrapper>
                                    <IconButton
                                        iconName="arrow-right"
                                        onClick={onClickVisitKeywordGenerator}
                                        isDisabled={isShowSubmitBtnTooltip}
                                    >
                                        {i18nFilter()("aquisitionintelligence.keywordResearch.homepage.seed.cta")}
                                    </IconButton>
                                </ButtonWrapper>
                            </PopupClickContainer>
                        </TopComponentWrapper>
                        <LinkWrapper>
                            <Text>
                                {i18nFilter()("digitalMarketing.findKeywords.keywordGenerator.link.title")}
                                &nbsp;<a onClick={onExample1Click}>{i18nFilter()(example1)}</a>
                                &nbsp;
                                {i18nFilter()("digitalMarketing.findKeywords.topKeywords.link.or")}
                                &nbsp;
                                <a onClick={onExample2Click}>{i18nFilter()(example2)}</a>
                            </Text>
                        </LinkWrapper>
                    </div>
                }
            />
        </>
    );
};
